/**
 * MyAgent LLM SDK - Main Entry Point
 *
 * Usage:
 *   import createLlmSdk from 'myagent_llm_sdk'
 *   const sdk = createLlmSdk({ httpClient, availableProviders: ['openai', 'gemini'] })
 *   const result = await sdk.chat({ provider: 'openai', apiKey, model, messages })
 */

// Client factories
import { createOpenaiClient } from './client/openai/index.js'
import { createGeminiClient } from './client/gemini/index.js'
import { createOllamaClient } from './client/ollama/index.js'

// Provider and unified interface factories
import { createProviders } from './unify/llm/providers.js'
import { createUnifiedInterface } from './unify/llm/base.js'

// Model selection
import { setAvailableProviders } from './unify/llm/model-selector.js'

/**
 * Create LLM SDK instance
 * @param {Object} options
 * @param {Function} options.httpClient - HTTP request function
 * @param {string[]} [options.availableProviders] - Providers with configured API keys
 * @returns {Object} SDK instance with chat and execute methods
 */
export default function createLlmSdk({ httpClient, availableProviders: configuredProviders = [] }) {
  if (!httpClient || typeof httpClient !== 'function') {
    throw new Error('httpClient is required and must be a function')
  }

  // Create raw clients
  const openaiClient = createOpenaiClient(httpClient)
  const geminiClient = createGeminiClient(httpClient)
  const ollamaClient = createOllamaClient(httpClient)

  // Create provider adapters
  const providers = createProviders({ openaiClient, geminiClient, ollamaClient })

  // Create unified interface
  const { chat, chatSingleStep, chatWithTools, listModels, execute, createAgent } = createUnifiedInterface(providers)

  // Configure model selector with available providers
  if (configuredProviders.length > 0) {
    setAvailableProviders(configuredProviders)
  }

  return {
    chat,
    chatSingleStep,
    chatWithTools,
    listModels,
    execute,
    createAgent,
  }
}
